import { Page } from '@roc/feature-app-core';
import { Route } from 'react-router';

import { LoanSubType } from '@roc/feature-utils';
import { ProductSelector } from '../productSelector';
import { FixFlipQuote } from '../fixflipQuote';
import { GroundUpQuote } from '../groundUpQuote';
import { StabilizedBridgeQuote } from '../stabilizedBridgeQuote';
import { SinglePropertyRentalQuote } from '../singlePropertyRentalQuote';
import { EditFixFlip } from '../../editLoan/EditFixFlip';
import { EditGroundUp } from '../../editLoan/EditGroundUp';
import { EditStabilizedBridge } from '../../editLoan/EditStabilizedBridge';
import { EditSingleProperty } from '../../editLoan/EditSingleProperty';
import { EditRentalPortfolio } from '../../editLoan/EditRentalPortfolio';
import { RentalPortfolioQuote } from '../rentalPortfolioQuote';

const ONE_TOOL = 'one-tool';

export const routeByLoanSubType = {
  [LoanSubType.FIX_AND_FLIP]: 'fixFlip',
  [LoanSubType.FIX_AND_FLIP_PRO]: 'fixFlipPro',
  [LoanSubType.GROUND_UP]: 'groundUp',
  [LoanSubType.MULTIFAMILY_BRIDGE_5PLUS]: 'multifamilyBridge',
  [LoanSubType.SINGLE_PROPERTY]: 'singleProperty',
  [LoanSubType.SINGLE_PROPERTY_MULTIFAMILY]: 'singleProperty',
  [LoanSubType.SINGLE_PROPERTY_MIXED_USE]: 'singleProperty',
  [LoanSubType.RENTAL_PORTFOLIO]: 'rentalPortfolio',
  [LoanSubType.MULTIFAMILY_TERM_PORTFOLIO]: 'rentalPortfolio',
  [LoanSubType.MIXED_USE_PORTFOLIO]: 'rentalPortfolio',
  [LoanSubType.STABILIZED_BRIDGE]: 'stabilizedBridge',
};

export const editRouteByLoanSubType = {
  [LoanSubType.FIX_AND_FLIP]: 'editFixFlip',
  [LoanSubType.FIX_AND_FLIP_PRO]: 'editFixFlip',
  [LoanSubType.GROUND_UP]: 'editGroundUp',
  [LoanSubType.STABILIZED_BRIDGE]: 'editStabilizedBridge',
  [LoanSubType.SINGLE_PROPERTY]: 'editSingleProperty',
  [LoanSubType.RENTAL_PORTFOLIO]: 'editRentalPortfolio',
};

export const getOneToolRoutesConfig = (basePath: string, baseUrl: string) => {
  return {
    oneTool: {
      path: `${basePath}/${ONE_TOOL}`,
      url: `${baseUrl}/${ONE_TOOL}`,
      documentTitle: 'Product Selector',
    },
    fixFlip: (quoteId?) => ({
      path: `${basePath}/${ONE_TOOL}/fix-flip/:quoteId?`,
      url: `${baseUrl}/${ONE_TOOL}/fix-flip/${quoteId ?? ''}`,
      documentTitle: 'Fix & Flip Quote',
    }),
    groundUp: (quoteId?) => ({
      path: `${basePath}/${ONE_TOOL}/ground-up/:quoteId?`,
      url: `${baseUrl}/${ONE_TOOL}/ground-up/${quoteId ?? ''}`,
      documentTitle: 'Ground Up Quote ',
    }),
    stabilizedBridge: (quoteId?) => ({
      path: `${basePath}/${ONE_TOOL}/stabilized-bridge/:quoteId?`,
      url: `${baseUrl}/${ONE_TOOL}/stabilized-bridge/${quoteId ?? ''}`,
      documentTitle: 'Stabilized Bridge Quote ',
    }),
    singleProperty: (quoteId?) => ({
      path: `${basePath}/${ONE_TOOL}/single-property/:quoteId?`,
      url: `${baseUrl}/${ONE_TOOL}/single-property/${quoteId ?? ''}`,
      documentTitle: 'Single Property Rental Quote',
    }),
    rentalPortfolio: (quoteId?) => ({
      path: `${basePath}/${ONE_TOOL}/rental-portfolio/:quoteId?`,
      url: `${baseUrl}/${ONE_TOOL}/rental-portfolio/${quoteId ?? ''}`,
      documentTitle: 'Rental Portfolio Quote',
    }),
    editFixFlip: (loanId?) => ({
      path: `${basePath}/${ONE_TOOL}/edit-fix-flip/:loanId?`,
      url: `${baseUrl}/${ONE_TOOL}/edit-fix-flip/${loanId ?? ''}`,
      documentTitle: 'Edit Fix & Flip Loan',
    }),
    editGroundUp: (loanId?) => ({
      path: `${basePath}/${ONE_TOOL}/edit-ground-up/:loanId?`,
      url: `${baseUrl}/${ONE_TOOL}/edit-ground-up/${loanId ?? ''}`,
      documentTitle: 'Edit Ground Up Loan',
    }),
    editStabilizedBridge: (loanId?) => ({
      path: `${basePath}/${ONE_TOOL}/edit-stabilized-bridge/:loanId?`,
      url: `${baseUrl}/${ONE_TOOL}/edit-stabilized-bridge/${loanId ?? ''}`,
      documentTitle: 'Edit Stabilized Bridge Loan',
    }),
    editSingleProperty: (loanId?) => ({
      path: `${basePath}/${ONE_TOOL}/edit-single-property/:loanId?`,
      url: `${baseUrl}/${ONE_TOOL}/edit-single-property/${loanId ?? ''}`,
      documentTitle: 'Edit Single Property Rental Loan',
    }),
    editRentalPortfolio: (loanId?) => ({
      path: `${basePath}/${ONE_TOOL}/edit-rental-portfolio/:loanId?`,
      url: `${baseUrl}/${ONE_TOOL}/edit-rental-portfolio/${loanId ?? ''}`,
      documentTitle: 'Edit Rental Portfolio Loan',
    }),
  };
};

export const getOneToolRoutes = (basePath: string, baseUrl: string) => {
  const config = getOneToolRoutesConfig(basePath, baseUrl);
  return {
    oneTool: (
      <Route exact path={config.oneTool.path}>
        <Page routeDefinition={config.oneTool}>
          <ProductSelector />
        </Page>
      </Route>
    ),
    fixFlip: (
      <Route exact path={config.fixFlip().path}>
        <Page routeDefinition={config.fixFlip()}>
          <FixFlipQuote />
        </Page>
      </Route>
    ),
    groundUp: (
      <Route exact path={config.groundUp().path}>
        <Page routeDefinition={config.groundUp()}>
          <GroundUpQuote />
        </Page>
      </Route>
    ),
    stabilizedBridge: (
      <Route exact path={config.stabilizedBridge().path}>
        <Page routeDefinition={config.stabilizedBridge()}>
          <StabilizedBridgeQuote />
        </Page>
      </Route>
    ),
    singleProperty: (
      <Route exact path={config.singleProperty().path}>
        <Page routeDefinition={config.singleProperty()}>
          <SinglePropertyRentalQuote />
        </Page>
      </Route>
    ),
    editFixFlip: (
      <Route exact path={config.editFixFlip().path}>
        <Page routeDefinition={config.editFixFlip()}>
          <EditFixFlip />
        </Page>
      </Route>
    ),
    editGroundUp: (
      <Route exact path={config.editGroundUp().path}>
        <Page routeDefinition={config.editGroundUp()}>
          <EditGroundUp />
        </Page>
      </Route>
    ),
    editStabilizedBridge: (
      <Route exact path={config.editStabilizedBridge().path}>
        <Page routeDefinition={config.editStabilizedBridge()}>
          <EditStabilizedBridge />
        </Page>
      </Route>
    ),
    editSingleProperty: (
      <Route exact path={config.editSingleProperty().path}>
        <Page routeDefinition={config.editSingleProperty()}>
          <EditSingleProperty />
        </Page>
      </Route>
    ),
    editRentalPortfolio: (
      <Route exact path={config.editRentalPortfolio().path}>
        <Page routeDefinition={config.editRentalPortfolio()}>
          <EditRentalPortfolio />
        </Page>
      </Route>
    ),
    rentalPortfolio: (
      <Route exact path={config.rentalPortfolio().path}>
        <Page routeDefinition={config.rentalPortfolio()}>
          <RentalPortfolioQuote />
        </Page>
      </Route>
    ),
  };
};
